import React, { useMemo, useState } from 'react';
import { REVIEWABLE_RESOURCE_TYPES } from '@lifeomic/phc-web-toolkit/src/util/extractedResourceReview';
import { getDocumentExtractionV2ReviewState } from './ExtractedResourceProvenance';
import {
  getExtractedResourceDisplay,
  useUnreviewedExtractedResources,
  type ExtractedFhirResource,
  type ReviewableResourceType,
  type UnreviewedExtractedResource,
} from './useUnreviewedExtractedResources';
import { ExtractedResourceReviewModal } from './ExtractedResourceReviewModal';
import { useReviewableResourceTypeLabel } from './extractedResourceReviewTypeLabels';

export { REVIEWABLE_RESOURCE_TYPES };

const reviewKey = (item: { resourceType: string; id: string }): string =>
  `${item.resourceType}/${item.id}`;

/**
 * The fields the review decision needs from a clicked row. Kept permissive so
 * each tab's resource type (Condition, Immunization, MedicationStatement, …)
 * can be passed without fighting the FHIR union's broad `code` typing.
 */
export type ReviewableResourceInput = fhir.Resource & {
  id?: string;
  extension?: fhir.Extension[];
  identifier?: Array<{ system?: string; value?: string }>;
};

/**
 * Whether clicking a resource row should open the full-screen side-by-side
 * source-document review experience: only AI-extracted resources that still
 * need clinician review qualify. Approved extractions and non-extracted
 * resources fall back to their normal detail modal.
 */
export const shouldOpenExtractedReview = (
  resource: ReviewableResourceInput,
  resourceType: string,
): boolean =>
  REVIEWABLE_RESOURCE_TYPES.has(resourceType) &&
  getDocumentExtractionV2ReviewState(resource) === 'needs-review';

/**
 * Build the `UnreviewedExtractedResource` payload the review modal expects from
 * a single clicked resource row.
 */
export const toUnreviewedExtractedResource = (
  resource: ReviewableResourceInput,
  resourceType: ReviewableResourceType,
): UnreviewedExtractedResource => {
  const extractedResource = resource as ExtractedFhirResource;
  return {
    id: resource.id ?? '',
    resourceType,
    display: getExtractedResourceDisplay(extractedResource),
    resource: extractedResource,
  };
};

type UseExtractedResourceReviewOptions = {
  memberId: string;
  /** Called after the review modal closes so the tab can refresh its rows. */
  onClose?: () => void;
};

type UseExtractedResourceReviewResult = {
  /**
   * Opens the side-by-side review modal for a needs-review extracted resource.
   * Returns `true` when it opened (caller should not open its own modal), or
   * `false` when the resource is not a needs-review extraction.
   */
  openReviewFor: (
    resource: ReviewableResourceInput,
    resourceType: string,
  ) => boolean;
  /** The review modal element, or `null` when nothing is being reviewed. */
  reviewModal: React.ReactNode;
};

/**
 * Encapsulates the full-screen extracted-resource review experience for the
 * member-detail clinical tabs, mirroring the Zori Pulse dashboard: clicking a
 * needs-review AI extraction opens the source document side-by-side with the
 * extracted details and approve/reject controls. Resources sharing a source
 * document are grouped into a single review queue.
 */
export const useExtractedResourceReview = ({
  memberId,
  onClose,
}: UseExtractedResourceReviewOptions): UseExtractedResourceReviewResult => {
  const typeLabelFor = useReviewableResourceTypeLabel();
  const { resources: needsReviewResources } =
    useUnreviewedExtractedResources(memberId);
  const [resolvedReviewKeys, setResolvedReviewKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [selected, setSelected] = useState<UnreviewedExtractedResource | null>(
    null,
  );

  const visibleNeedsReview = useMemo(
    () =>
      needsReviewResources.filter(
        item => !resolvedReviewKeys.has(reviewKey(item)),
      ),
    [needsReviewResources, resolvedReviewKeys],
  );

  // Ensure the clicked resource is part of the review queue even before the
  // member-wide needs-review query resolves, but drop it once it has been
  // resolved so the modal can auto-close when the queue empties.
  const items = useMemo(() => {
    if (!selected) {
      return visibleNeedsReview;
    }
    if (resolvedReviewKeys.has(reviewKey(selected))) {
      return visibleNeedsReview;
    }
    return visibleNeedsReview.some(
      item => reviewKey(item) === reviewKey(selected),
    )
      ? visibleNeedsReview
      : [selected, ...visibleNeedsReview];
  }, [visibleNeedsReview, selected, resolvedReviewKeys]);

  const openReviewFor = (
    resource: ReviewableResourceInput,
    resourceType: string,
  ): boolean => {
    if (!shouldOpenExtractedReview(resource, resourceType)) {
      return false;
    }
    setSelected(
      toUnreviewedExtractedResource(
        resource,
        resourceType as ReviewableResourceType,
      ),
    );
    return true;
  };

  // Reached only after the modal's feedback POST has persisted the decision.
  // Analytics for this surface are emitted at the persistence seam
  // (`ExtractedResourceProvenance` with `member_clinical_tab`).
  const markReviewResolved = (item: {
    resourceType: ReviewableResourceType;
    id: string;
    resolution: 'approved' | 'rejected';
  }) => {
    setResolvedReviewKeys(current => {
      const next = new Set(current);
      next.add(reviewKey(item));
      return next;
    });
  };

  const handleDismiss = () => {
    setSelected(null);
    onClose?.();
  };

  const reviewModal = selected ? (
    <ExtractedResourceReviewModal
      items={items}
      selected={selected}
      subjectId={memberId}
      typeLabelFor={typeLabelFor}
      reviewAnalyticsSurface="member_clinical_tab"
      onResolveItem={markReviewResolved}
      onDismiss={handleDismiss}
    />
  ) : null;

  return { openReviewFor, reviewModal };
};
